import React, {useEffect, useId, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {Spinner} from '../../Spinner';

// Las cuatro posiciones del knob, en orden de menos a más autonomía. Espejan
// `core/services/client_memory_autonomy.py` del backend, que es el SSOT: si allá se agrega una,
// acá hay que sumar su par de claves i18n (`level.X` + `level.X_desc`).
//
// El ORDEN no es cosmético: es el eje del riel, el que decide el color por riesgo, y el que decide
// si un cambio SUBE la autonomía —de eso depende que se ofrezca aprobar la cola que ya estaba
// esperando (`useAutonomyRaiseOffer`).
export const AUTONOMY_LEVELS = ['MANUAL', 'VERIFIED', 'AUTONOMOUS', 'FULL'];
export const AUTONOMY_RECOMMENDED = 'VERIFIED';

// La posición donde la red de seguridad cambia de forma: «Todo de una» deja entrar solos los hechos
// sensibles, así que su ⓘ no puede ser el mismo texto que el de las otras tres.
const AUTONOMY_FULL = 'FULL';

// Cuánto sobrevive el aviso de «Todo de una» después de soltar el riel sin permiso. Seis segundos
// alcanzan para leerlo sin que quede pegado en la tarjeta.
const DENIED_HINT_MS = 6000;

/**
 * El control «¿Cuánto aprueba Dashbo solo?» (card 1EW5v3Ba).
 *
 * Una sola pieza para las dos superficies: la configuración de la agencia (`scope='agency'`, hoy
 * OCULTA — ver abajo) y la Memoria de un cliente (`scope='client'`).
 *
 * 🔴 **El ajuste es POR CLIENTE, y punto** (decisión de fede el 08/09, card 8uu3IdNd, que cambia la
 * del 07/09). El 07/09 se shipeó default de agencia + override por cliente, con un interruptor
 * «Como la agencia» y un link al ajuste global. Pablo: «yo lo mantendría por ahora a nivel
 * cliente… tiene sentido que simplifiquemos; puede ser algo que en algún momento nos pidan y ahí
 * lo agreguemos». fede: «capaz la compliqué al pedo».
 *
 * Lo que se sacó es la UI, NO el modelo: el backend conserva el default de la agencia como valor
 * oculto y `AgencyClient.memory_autonomy()` sigue siendo el único resolver. Por eso este control
 * recibe el nivel EFECTIVO (lo que rige de verdad para ese cliente, herencia ya resuelta) y al
 * mover el riel guarda un valor concreto: la primera vez que alguien lo toca, ese cliente deja de
 * heredar. Volver a ofrecer los dos niveles es devolver el interruptor y el link, no rehacer nada.
 *
 * **Es el riel del mockup** (`dashbo-ia-booster-lab_22.html:965`), no una lista de tarjetas. La
 * primera versión usó radios para poder comparar las tres descripciones sin mover el estado, y eso
 * salió mal por dos motivos: (a) en el rail del Recorrido el bloque ocupaba la columna entera y
 * dominaba una tarjeta que convive con otras dos —el QA ya había marcado que hasta la nota al pie
 * pesaba más que las descripciones—, y (b) el argumento de fondo era falso. El mockup **no** guarda
 * al mover: `oninput` sólo cambia la descripción y `onchange` guarda, y en un `range` `change`
 * dispara **al soltar**. O sea que arrastrar para leer las tres posiciones ya era gratis.
 *
 * ⚠️ Esa separación **no se puede copiar tal cual en React**: ahí `onInput` y `onChange` son el
 * mismo evento sintético y los dos corren con el `input` nativo. El equivalente honesto es
 * previsualizar en `onChange` y guardar en los eventos de SOLTAR (puntero, tecla, `blur`).
 *
 * Lo que el riel NO puede expresar y por eso sigue afuera de él: **la red de seguridad**, detrás de
 * un ⓘ. Es lo que impide que «Casi todo» se lea como «Dashbo hace lo que quiere», pero no tiene por
 * qué ocupar seis líneas fijas.
 *
 * **La cuarta posición del mockup existe desde el 09/09** (card 8uu3IdNd): «Todo de una» deja
 * entrar los hechos SENSIBLES —riesgos y condiciones comerciales— y la confianza LOW. Se había
 * descartado el 07/09 por eso mismo; la pidió Pablo para probarla con Pau Rouge (Bakián). Lo único
 * que ninguna posición mueve es la cita que se buscó y NO está —el detector de alucinación—, que en
 * material HABLADO ni siquiera corre: para el caso que motivó la posición, «Todo de una» ya es
 * literalmente todo. El porqué completo está en `core/services/client_memory_autonomy.py`.
 *
 * 🔴 Por eso el ⓘ **no puede ser un texto fijo**: hasta el 09/09 decía «en todas las opciones los
 * sensibles esperan tu ok», y en «Todo de una» eso es falso.
 */
export function MemoryAutonomyControl({
    // Sin `scope`: al salir el nivel agencia de la UI, el control se comporta igual en las dos
    // superficies. Si vuelve el ajuste global, vuelve el prop junto con el interruptor.
    // `canSetFull` arranca en `true` a propósito: ver el comentario del hook. Sólo un `false`
    // explícito del backend bloquea la posición.
    // `knobRef`: quien monta el control puede devolverle el foco al riel cuando cierra un modal que
    // salió de moverlo — el riel se deshabilita mientras guarda, así que para cuando el modal abre el
    // foco ya se fue al `body` (QA-VIS-knob2-09).
    value, canEdit = true, canSetFull = true, saving = false, onChange, compact = false, knobRef,
}) {
    const {t} = useTranslation();
    // `value` es el nivel EFECTIVO: para un cliente, lo que rige de verdad (su override, o lo que
    // hereda del default oculto de la agencia). El control no distingue de dónde vino — sólo lo
    // muestra y guarda lo que el usuario elige.
    const effective = value || AUTONOMY_RECOMMENDED;

    // La posición que se está PREVISUALIZANDO mientras se arrastra: es lo que deja leer las tres
    // descripciones sin guardar ninguna. Sin esto, recorrer el riel costaría tres PUT.
    const [preview, setPreview] = useState(null);
    const shownRaw = preview || effective;
    // ⚠️ Un nivel que este bundle NO conoce no puede caer en la posición 0. `indexOf` devuelve -1 y
    // el `Math.max(0, …)` que había acá lo dibujaba como «Revisas todo» —la más conservadora, y en
    // verde— mientras regía otra cosa: falla en la dirección que **subestima** el riesgo. Y no es
    // hipotético: pasa cada vez que el backend estrena una posición antes de que el frontend la
    // conozca, que es exactamente lo que le pasó al Command Center con «Todo de una». Cae al
    // recomendado, así que el rótulo, la descripción, el `aria-valuetext`, los ticks y el color
    // hablan todos de la MISMA posición en vez de mezclar una conocida con una que no.
    // Hallazgo C-knob-18 del review del 10/09.
    const known = AUTONOMY_LEVELS.includes(shownRaw);
    const shown = known ? shownRaw : AUTONOMY_RECOMMENDED;
    const index = AUTONOMY_LEVELS.indexOf(shown);
    const locked = !canEdit || saving;
    // El id del texto de la red de seguridad, para que el lector de pantalla lo anuncie como la
    // descripción del ⓘ y no como un párrafo suelto.
    const footnoteId = useId();

    // 🔴 En React `onInput` y `onChange` son el MISMO evento sintético: los dos se disparan con el
    // `input` nativo, así que no sirven para separar «arrastrar» de «soltar» como hace el mockup en
    // HTML plano. Acá `onChange` sólo previsualiza y el guardado va en los eventos de SOLTAR —
    // puntero, tecla y `blur`—, que es lo que de verdad significa «ya elegí».
    // ¿La posición que se está MIRANDO es una que este usuario no puede activar? Se deja mirar y
    // leer su descripción —para eso existe la previsualización— pero no se guarda.
    const blockedByRole = shown === AUTONOMY_FULL && !canSetFull;

    // ⚠️ El aviso «esta posición la activa un admin» vivía atado a `preview`, así que **desaparecía en
    // el mismo instante en que se soltaba el riel**: el riel volvía a «Solo lo comprobado», no se
    // guardaba nada y el único cartel que explicaba por qué se iba con él. Con el teclado (`Fin`) no
    // se llegaba a leer nunca. Queda unos segundos después de soltar, que es cuando hace falta
    // (QA 27/09, «casos de uso que faltaba contemplar» 2).
    const [denied, setDenied] = useState(false);
    const deniedTimer = useRef(null);
    useEffect(() => () => clearTimeout(deniedTimer.current), []);

    const clearDenied = () => {
        clearTimeout(deniedTimer.current);
        setDenied(false);
    };

    /**
     * `fromKeyboard` dice de qué gesto vino el guardado, y viaja al caller en el segundo argumento de
     * `onChange`. Lo necesita quien abra un modal a partir de este cambio: devolverle el foco al riel
     * después de cerrarlo sólo es correcto si el usuario estaba EN el riel con el teclado — si llegó
     * con el mouse, el foco puesto por código no se ve (`:focus-visible` no se enciende) y la
     * siguiente flecha mueve el riel y GUARDA, que es lo último que el usuario espera al apretar «→»
     * para aprobar una carta (C-knob3-02, review del 27/09).
     */
    const commit = (fromKeyboard) => {
        const next = preview;
        setPreview(null);
        if (locked || !next) return;
        // El backend contesta 403 igual; esto evita que el usuario llegue hasta ahí. Soltar el riel
        // en «Todo de una» sin permiso no guarda nada y el hint de abajo dice por qué.
        if (next === AUTONOMY_FULL && !canSetFull) {
            setDenied(true);
            clearTimeout(deniedTimer.current);
            deniedTimer.current = setTimeout(() => setDenied(false), DENIED_HINT_MS);
            return;
        }
        // ⚠️ El aviso se apaga ANTES de guardar otra posición. Vivía sólo con su temporizador, así que
        // soltar en «Todo de una» sin permiso y dentro de esos 6 s elegir «Casi todo» dejaba, debajo
        // de la descripción de «Casi todo», el cartel que dice que en esa posición los sensibles
        // dejan de esperar — falso para la posición que quedó guardada (C-knob3-05).
        clearDenied();
        if (next !== effective) onChange(next, {fromKeyboard: !!fromKeyboard});
    };

    return (
        // El color del riel es el NIVEL DE RIESGO de la posición que se está mirando (pedido de
        // Pablo, 08/09): verde en «Revisas todo», dos amarillos en el medio, rojo en «Todo de una».
        // Va como modificador por ÍNDICE y no por nombre de nivel — el eje es el orden, así que una
        // posición nueva en el medio corre los colores sola en vez de dejar un hueco sin pintar.
        <div className={`mautonomy -risk-${index} ${compact ? '-compact' : ''}`}>
            <div className="mautonomy__head">
                <h4 className="mautonomy__title">{t('memory_autonomy.title')}</h4>
                {/* La red de seguridad, a un hover. Usa el tooltip del design system, no `title`.
                    El texto es el de la posición que se está MIRANDO (`shown`), no el de la que
                    rige: arrastrando el riel se lee qué deja entrar cada una antes de soltarlo, que
                    es exactamente para lo que existe la previsualización — y en «Todo de una» eso
                    es lo único que avisa que los sensibles dejan de esperar. */}
                {/* ⚠️ `tabIndex` + `aria-label` + `aria-describedby`, igual que `FactTypeHelp`: el ⓘ
                    abría sólo con el mouse, así que el ÚNICO aviso de que en «Todo de una» los
                    sensibles dejan de esperar era inalcanzable con teclado y en táctil
                    (QA-VIS-knob2-11). El `:focus-visible` de `.info-icon-container` ya sabe abrirlo;
                    lo que faltaba era un disparador que pudiera recibir el foco. */}
                {/* `role="img"`: ARIA 1.2 no deja ponerle nombre a un `span` genérico, así que sin rol
                    el `aria-label` lo ignoran varios lectores de pantalla y axe lo marca. Mismo
                    arreglo que ya tiene `ControlLinesCard` (C-SEGFE-06 · acá, C-knob3-06). */}
                <span className="info-icon-container mautonomy__info"
                      role="img"
                      tabIndex={0}
                      aria-label={t('memory_autonomy.footnote_label')}
                      aria-describedby={footnoteId}>
                    <FaIcon iconName="info-circle"/>
                    <span className="warning-tooltip -below-right -rich">
                        <span className="warning-tooltip-body">
                            <span className="warning-tooltip-text" id={footnoteId}>
                                {t(shown === AUTONOMY_FULL
                                    ? 'memory_autonomy.footnote_full'
                                    : 'memory_autonomy.footnote')}
                            </span>
                        </span>
                    </span>
                </span>
                {/* «Guardando» era sólo el riel atenuado: sin spinner, un PUT lento se lee como que el
                    control se trabó (QA-VIS-knob2-17). Va en el encabezado, que ya es flex, para no
                    mover de lugar nada de lo que está debajo. */}
                {saving && <Spinner color="positive small"/>}
            </div>

            <input
                type="range"
                className="mautonomy__knob"
                ref={knobRef}
                min={0}
                max={AUTONOMY_LEVELS.length - 1}
                step={1}
                value={index}
                disabled={locked}
                aria-label={t('memory_autonomy.title')}
                // Un `range` anuncia «1 de 3», que no dice nada: el lector de pantalla necesita el
                // nombre de la posición, igual que quien la ve.
                aria-valuetext={t(`memory_autonomy.level.${shown}`)}
                onChange={(e) => {
                    const next = AUTONOMY_LEVELS[Number(e.target.value)];
                    setPreview(next);
                    // Mirar otra posición también apaga el aviso: el cartel habla de «Todo de una»
                    // y quedaría debajo de la descripción de otra (C-knob3-05).
                    if (next !== AUTONOMY_FULL) clearDenied();
                }}
                onPointerUp={() => commit(false)}
                onKeyUp={() => commit(true)}
                onBlur={() => commit(false)}
            />

            <div className="mautonomy__ticks" aria-hidden="true">
                {AUTONOMY_LEVELS.map((level) => (
                    <span key={level} className={level === shown ? 'is-on' : ''}>
                        {t(`memory_autonomy.level.${level}`)}
                        {level === AUTONOMY_RECOMMENDED && <i className="mautonomy__dot"/>}
                    </span>
                ))}
            </div>

            {/* La descripción de la posición que se está mirando. `min-height` en el SCSS para que
                el bloque no salte de alto al cambiar de texto (el mockup hace lo mismo). */}
            <p className="mautonomy__say">
                <b>{t(`memory_autonomy.level.${shown}`)}.</b>{' '}
                {t(`memory_autonomy.level.${shown}_desc`)}
                {/* `known` y no sólo la comparación: con un nivel desconocido el riel ya cayó al
                    recomendado para poder dibujarse, y encima ponerle la pastilla «recomendado»
                    sería afirmar algo sobre un estado que justamente no pudimos leer. */}
                {/* El espacio va acá y no como `margin-left` en el SCSS: un margen sobrevive al salto
                    de línea y dejaba la pastilla corrida 5px cuando bajaba a su propio renglón
                    (QA-VIS-knob2-21); un espacio de texto se come solo al envolver. */}
                {known && shown === AUTONOMY_RECOMMENDED && (
                    <>{' '}
                        <span className="mautonomy__badge">{t('memory_autonomy.recommended')}</span>
                    </>
                )}
            </p>

            {!canEdit && <p className="mautonomy__hint">{t('memory_autonomy.read_only_hint')}</p>}
            {/* Aparece al mirar «Todo de una» sin ser admin. No reemplaza al `read_only_hint`: ése
                es «no podés tocar nada», éste «podés tocar todo menos esta posición». */}
            {canEdit && (blockedByRole || denied) && (
                <p className="mautonomy__hint">{t('memory_autonomy.full_admin_only')}</p>
            )}
        </div>
    );
}

export default MemoryAutonomyControl;
